import React, { useEffect, useState } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MicOffIcon from '@mui/icons-material/MicOff';
import BrushIcon from '@mui/icons-material/Brush';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as DroneMode } from '@public/images/DroneMode.svg';
import { ReactComponent as LevelEditor } from '@public/images/LevelEditor.svg';
import IconPopover from '../../../IconPopover';
import ToolBoxItem from '../../../ToolBoxItem';
import {
  BrandingListModal,
  CreateBrandingModal,
  EditAreaPickerModal,
  NameBrandingModal,
  SelectBrandingModal,
  IBranding,
  mapBrandingFromApi,
} from '../../../BrandingModeModals';
import type { EditorFocus } from '../../../EditorMode/editorCategories';

import { useAdminTools } from './hooks';

import { useStyles } from './styles';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';
import BrandingsService from '@services/BrandingsService';
import OVERLAYS from '@constants/overlays';
import { env } from 'env';

const { REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE } = env;

const AdminTools = () => {
  const { translate, handleDroneMode, handleMuteAll, handleLevelEditor } =
    useAdminTools();

  const {
    currentEvent,
    currentRoom,
    currentParticipant,
    setCurrentRoom,
    setRoomSpots,
    setSelectedCustomizationUeId,
    isBrandingModeEnabled,
    setIsBrandingModeEnabled,
    currentBrandingId,
    setCurrentBrandingId,
    brandingActionRequest,
    setBrandingActionRequest,
    setBrandingEditorFocus,
  } = useMytaverseEvent();

  const classes = useStyles(isBrandingModeEnabled);
  const { sendMessageToUnreal } = useMytaverse();

  const [showBrandingListModal, setShowBrandingListModal] = useState(false);
  const [showCreateBrandingModal, setShowCreateBrandingModal] = useState(false);
  const [showNameBrandingModal, setShowNameBrandingModal] = useState(false);
  const [showSelectBrandingModal, setShowSelectBrandingModal] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [selectedCloneBrandingId, setSelectedCloneBrandingId] = useState<
    string | null
  >(null);

  const [editAreaPickerBrandingId, setEditAreaPickerBrandingId] = useState<
    string | null
  >(null);

  const [brandings, setBrandings] = useState<IBranding[]>([]);
  const [brandingsLoading, setBrandingsLoading] = useState(false);

  const mapId = currentRoom?.map?.mapId;

  const isBrandingFlowOpen =
    showBrandingListModal ||
    showCreateBrandingModal ||
    showSelectBrandingModal ||
    showNameBrandingModal ||
    editAreaPickerBrandingId !== null;

  useEffect(() => {
    if (!isBrandingFlowOpen) {
      setBrandings([]);
      return;
    }
    if (!mapId || !currentRoom?.roomId) {
      setBrandings([]);
      setBrandingsLoading(false);
      return;
    }
    setBrandingsLoading(true);

    const loadBrandings = async () => {
      try {
        const room = await EventsService.getRoom(currentRoom.roomId);
        if (room?.activeBrandingId) {
          setCurrentRoom((prev) =>
            prev && prev.roomId === room.roomId
              ? { ...prev, activeBrandingId: room.activeBrandingId }
              : prev,
          );
        }

        const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
        const activeBrandingId =
          room?.activeBrandingId ?? currentRoom?.activeBrandingId ?? null;
        const mapped = (res.brandings ?? []).map((b, i) =>
          mapBrandingFromApi(b, i, activeBrandingId),
        );
        setBrandings(mapped);

        const active = mapped.find((b) => b.isActive) || mapped[0];
        setCurrentBrandingId(active ? active.id : null);
      } catch {
        setBrandings([]);
      } finally {
        setBrandingsLoading(false);
      }
    };

    loadBrandings();
  }, [
    currentRoom?.activeBrandingId,
    currentRoom?.roomId,
    isBrandingFlowOpen,
    mapId,
    setCurrentBrandingId,
    setCurrentRoom,
  ]);

  const isLevelEditingEnabled =
    currentEvent?.world?.isLevelEditingEnabled ?? false;

  const handleOpenBrandingMode = () => {
    setShowBrandingListModal(true);
  };

  const handleOpenCreateBrandingModal = () => {
    setShowBrandingListModal(false);
    setShowCreateBrandingModal(true);
  };

  useEffect(() => {
    if (brandingActionRequest === 'library') {
      setShowBrandingListModal(true);
      setBrandingActionRequest(null);
    } else if (brandingActionRequest === 'create') {
      handleOpenCreateBrandingModal();
      setBrandingActionRequest(null);
    }
  }, [brandingActionRequest, setBrandingActionRequest]);

  const handleCloseCreateBrandingModal = () => {
    setShowCreateBrandingModal(false);
  };

  const handleCloseBrandingListModal = () => {
    setShowBrandingListModal(false);
  };

  const handleToggleBranding = async (id: string) => {
    let previousBrandings: IBranding[] = [];

    setBrandings((prev) => {
      previousBrandings = prev;
      return prev.map((branding) => ({
        ...branding,
        isActive: branding.id === id,
      }));
    });
    setCurrentBrandingId(id);

    const roomId = currentRoom?.roomId;

    if (!roomId) {
      return;
    }

    try {
      await BrandingsService.activateBranding(id, roomId);

      setCurrentRoom((prev) =>
        prev && prev.roomId === roomId
          ? { ...prev, activeBrandingId: id }
          : prev,
      );
    } catch {
      setBrandings(previousBrandings);
      const active =
        previousBrandings.find((b) => b.isActive) || previousBrandings[0];
      setCurrentBrandingId(active ? active.id : null);
    }
  };

  const enterBrandingMode = async () => {
    setIsBrandingModeEnabled(true);
    if (!isCloneMode) {
      sendMessageToUnreal({
        action: 'CLEAR_SPOTS',
      });
    }

    sendMessageToUnreal({
      action: 'ENTER_TO_CUSTOMIZATION_MODE',
    });

    if (currentRoom) {
      const roomSpots = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(roomSpots);
      setSelectedCustomizationUeId(null);
    }
  };

  const handleEditBranding = (id: string) => {
    setShowBrandingListModal(false);
    setEditAreaPickerBrandingId(id);
  };

  const handleCloseEditAreaPicker = () => {
    setEditAreaPickerBrandingId(null);
  };

  const handlePickEditArea = async (focus: EditorFocus) => {
    const id = editAreaPickerBrandingId;
    if (!id) {
      return;
    }
    setEditAreaPickerBrandingId(null);
    setBrandingEditorFocus(focus);
    await enterBrandingMode();
    await handleToggleBranding(id);
  };

  const handleCloneFromBranding = (id: string) => {
    setSelectedCloneBrandingId(id);
    setIsCloneMode(true);
    setShowBrandingListModal(false);
    setShowNameBrandingModal(true);
  };

  const handleDeleteBranding = async (id: string) => {
    const isActiveBranding =
      currentBrandingId === id ||
      brandings.some((b) => b.id === id && b.isActive);
    if (isActiveBranding) {
      return;
    }

    let previousBrandings: IBranding[] = [];
    let deletedInState = false;
    let nextActiveBrandingId: string | null = currentBrandingId;

    setBrandings((prev) => {
      previousBrandings = prev;

      const filtered = prev.filter((b) => b.id !== id);
      if (filtered.length === 0) {
        return prev;
      }

      deletedInState = true;

      const hasActive = filtered.some((b) => b.isActive);
      if (hasActive) {
        const active = filtered.find((b) => b.isActive);
        nextActiveBrandingId = active ? active.id : null;
        return filtered;
      }

      const withActive = filtered.map((b, idx) => ({
        ...b,
        isActive: idx === 0,
      }));
      nextActiveBrandingId = withActive.length ? withActive[0].id : null;
      return withActive;
    });

    if (!deletedInState) {
      return;
    }

    setCurrentBrandingId(nextActiveBrandingId || null);

    try {
      await BrandingsService.deleteBranding(id);
    } catch {
      setBrandings(previousBrandings);
    }
  };

  const handleStartFromScratch = () => {
    setShowCreateBrandingModal(false);
    setIsCloneMode(false);
    setShowNameBrandingModal(true);
  };

  const handleCloneFromExisting = () => {
    setShowCreateBrandingModal(false);
    setShowSelectBrandingModal(true);
  };

  const handleSelectBranding = (brandingId: string) => {
    setSelectedCloneBrandingId(brandingId);
    setShowSelectBrandingModal(false);
    setIsCloneMode(true);
    setShowNameBrandingModal(true);
  };

  const handleCloseSelectBrandingModal = () => {
    setShowSelectBrandingModal(false);
  };

  const handleBackFromNameModal = () => {
    setShowNameBrandingModal(false);
    if (isCloneMode) {
      setShowSelectBrandingModal(true);
    } else {
      setShowCreateBrandingModal(true);
    }
  };

  const handleCloseNameModal = () => {
    setShowNameBrandingModal(false);
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleContinueWithName = async (name: string) => {
    setShowNameBrandingModal(false);

    const mapId = currentRoom?.map?.mapId;
    const authorEmail = currentParticipant?.email;

    if (!mapId) {
      await enterBrandingMode();
      setIsCloneMode(false);
      setSelectedCloneBrandingId(null);
      return;
    }

    try {
      const created = await BrandingsService.createBranding({
        mapId,
        name,
        ...(isCloneMode && selectedCloneBrandingId
          ? { parentBrandingId: selectedCloneBrandingId }
          : {}),
        ...(authorEmail ? { author: authorEmail } : {}),
      });
      const mapped = mapBrandingFromApi(created, 0);
      setCurrentBrandingId(mapped.id);

      const roomId = currentRoom?.roomId;
      if (roomId) {
        try {
          await BrandingsService.activateBranding(mapped.id, roomId);
          setCurrentRoom((prev) =>
            prev && prev.roomId === roomId
              ? { ...prev, activeBrandingId: mapped.id }
              : prev,
          );
        } catch {
          /* */
        }
      }

      const res = await BrandingsService.getBrandings({ mapId, limit: 100 });
      const activeBrandingId = mapped.id;
      const refreshed = (res.brandings ?? []).map((b, i) =>
        mapBrandingFromApi(b, i, activeBrandingId),
      );
      setBrandings(refreshed);
    } catch {
      /* empty */
    }

    await enterBrandingMode();
    setIsCloneMode(false);
    setSelectedCloneBrandingId(null);
  };

  const handleLeaveBrandingMode = () => {
    setShowBrandingListModal(false);
    setRoomSpots([]);
    setIsBrandingModeEnabled(false);
    setCurrentBrandingId(null);
    sendMessageToUnreal({
      action: 'LEAVE_CUSTOMIZATION_MODE',
    });
  };

  const brandingModeIcon = <BrushIcon sx={classes.brandingModeIcon} />;

  return (
    <>
      <IconPopover
        icon={<ManageAccountsIcon sx={classes.icon} />}
        label={translate('adminToolbox.toolboxLabel')}
        position={{ vertical: 'top', horizontal: 'right' }}
        analyticsName="admin"
        overlayName={OVERLAYS.ADMIN_TOOLS}
        dataTestId="admin-tools-launcher-btn"
      >
        {({ onClose }) => (
          <Box component="div" sx={classes.container}>
            <Box component="div" sx={classes.header}>
              <Typography sx={classes.title}>
                {translate('adminToolbox.title', 'Admin Tools')}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={classes.closeButton}
                data-testid="admin-tools-popover-close-btn"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box component="div" sx={classes.tools}>
              <ToolBoxItem
                Icon={brandingModeIcon}
                title={translate('adminToolbox.editorMode', 'Editor Mode')}
                handleClick={handleOpenBrandingMode}
                dataTestId="admin-tools-editor-mode-btn"
              />
              <ToolBoxItem
                Icon={<DroneMode />}
                title={translate('adminToolbox.droneMode')}
                handleClick={handleDroneMode}
                dataTestId="admin-tools-drone-mode-btn"
              />
              {REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE === 'true' ? (
                <ToolBoxItem
                  Icon={<MicOffIcon />}
                  title={translate('adminToolbox.muteAll')}
                  handleClick={handleMuteAll}
                  dataTestId="admin-tools-mute-all-btn"
                />
              ) : null}
              {isLevelEditingEnabled ? (
                <ToolBoxItem
                  Icon={<LevelEditor />}
                  title={translate('adminToolbox.levelEditor')}
                  handleClick={() => {
                    handleLevelEditor();
                    onClose();
                  }}
                  dataTestId="admin-tools-level-editor-btn"
                />
              ) : null}
            </Box>
          </Box>
        )}
      </IconPopover>

      <BrandingListModal
        open={showBrandingListModal}
        onClose={handleCloseBrandingListModal}
        onLeaveBrandingMode={handleLeaveBrandingMode}
        onCreateNew={handleOpenCreateBrandingModal}
        brandings={brandings}
        brandingsLoading={brandingsLoading}
        onToggleBranding={handleToggleBranding}
        onEditBranding={handleEditBranding}
        onCloneBranding={handleCloneFromBranding}
        onDeleteBranding={handleDeleteBranding}
        currentBrandingId={currentBrandingId}
        eventName={currentEvent?.name}
      />

      <CreateBrandingModal
        open={showCreateBrandingModal}
        onClose={handleCloseCreateBrandingModal}
        onStartFromScratch={handleStartFromScratch}
        onCloneFromExisting={handleCloneFromExisting}
        hasExistingBrandings={brandings.length > 0}
      />

      <NameBrandingModal
        open={showNameBrandingModal}
        onClose={handleCloseNameModal}
        onBack={handleBackFromNameModal}
        onContinue={handleContinueWithName}
      />

      <SelectBrandingModal
        open={showSelectBrandingModal}
        onClose={handleCloseSelectBrandingModal}
        onSelect={handleSelectBranding}
        brandings={brandings}
      />

      <EditAreaPickerModal
        open={editAreaPickerBrandingId !== null}
        brandingName={
          brandings.find((b) => b.id === editAreaPickerBrandingId)?.name
        }
        onClose={handleCloseEditAreaPicker}
        onPick={handlePickEditArea}
      />
    </>
  );
};

export default AdminTools;
